import { useFormContext } from 'react-hook-form';
import { MenuItem, Skeleton, Stack, Typography } from '@mui/material';

import { ATSAutomationCreateTestIds } from 'data-testids/ATS';
import { AutomationDropdown } from './AutomationDropdown';
import { ErrorText, IndividualAutomationContainer } from 'features/Automations/styles';
import { getActionType, getAnswerOptions } from 'features/Automations/utils';
import { IndividualAutomationProps } from 'features/Automations/types';
import { useQueryGetApplicationStatusListData } from 'features/Automations/api/createQueries';
import auth from 'utils/auth';
import { QuestionType } from 'utils/constants';

export const IndividualAutomation: React.FC<IndividualAutomationProps> = ({ fieldName, index }) => {
  const {
    watch,
    formState: { errors },
  } = useFormContext();

  const districtId: number | undefined = auth.getUser()?.profile?.district.id;
  const questionType: QuestionType = watch(`automations.${index}.trigger.question_type`);
  const { data: applicationStatuses, isLoading } = useQueryGetApplicationStatusListData(districtId);
  const actionTypeOptions = getActionType();
  const answerOptions = getAnswerOptions(questionType);

  const fieldError = errors?.automations?.[index]?.trigger?.expected_answer;
  const actionTypeError = errors?.automations?.[index]?.action?.action_type;
  const newStatusError = errors?.automations?.[index]?.action?.new_status_id;
  const hasFieldError = fieldError || actionTypeError || newStatusError;

  return (
    <IndividualAutomationContainer
      spacing={2}
      data-testid={ATSAutomationCreateTestIds.INDIVIDUAL_AUTOMATION}
    >
      <Typography data-testid={ATSAutomationCreateTestIds.AUTOMATION_COUNT_TEXT} variant="body1">
        Automation {index + 1}
      </Typography>
      {isLoading ? (
        <Skeleton
          data-testid={ATSAutomationCreateTestIds.AUTOMATION_SKELETON}
          variant="rectangular"
          sx={{ height: 50 }}
        />
      ) : (
        <Stack>
          <Stack direction="row" alignItems="center" sx={{ mt: '8px !important' }}>
            <Typography>If a candidate responds:</Typography>
            <AutomationDropdown
              dataTestId={ATSAutomationCreateTestIds.ANSWER_OPTION_DROPDOWN}
              name={fieldName}
              defaultValue={answerOptions[0]?.label}
            >
              {answerOptions.map((option) => (
                <MenuItem key={option.id} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
            <Typography>then</Typography>

            <AutomationDropdown
              dataTestId={ATSAutomationCreateTestIds.ACTION_TYPE_DROPDOWN}
              name={`automations.${index}.action.action_type`}
              defaultValue={actionTypeOptions?.[0]?.id || ''}
            >
              {actionTypeOptions?.map((actionType) => (
                <MenuItem key={actionType.id} value={actionType.id}>
                  {actionType.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
            <Typography>to</Typography>
            <AutomationDropdown
              dataTestId={ATSAutomationCreateTestIds.NEW_STATUS_DROPDOWN}
              name={`automations.${index}.action.new_status_id`}
              defaultValue={applicationStatuses?.[0]?.id || undefined}
            >
              {applicationStatuses?.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.label}
                </MenuItem>
              ))}
            </AutomationDropdown>
          </Stack>
          {hasFieldError && (
            <ErrorText data-testid={ATSAutomationCreateTestIds.MISSING_FIELD_ERROR_TEXT}>
              Please complete all required fields for this automation
            </ErrorText>
          )}
        </Stack>
      )}
    </IndividualAutomationContainer>
  );
};
